import React, { useCallback, useRef, useState } from 'react';

export const RAIL_WIDTH_DEFAULT = 280;
export const RAIL_WIDTH_MIN = 200;
export const RAIL_WIDTH_MAX = 420;

function clampWidth(value: number): number {
  return Math.min(RAIL_WIDTH_MAX, Math.max(RAIL_WIDTH_MIN, Math.round(value)));
}

function readWidth(storageKey: string): number {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null) return RAIL_WIDTH_DEFAULT;
    const value = Number(raw);
    return Number.isFinite(value) ? clampWidth(value) : RAIL_WIDTH_DEFAULT;
  } catch {
    return RAIL_WIDTH_DEFAULT;
  }
}

function writeWidth(storageKey: string, value: number) {
  try {
    localStorage.setItem(storageKey, String(value));
  } catch {
    /* storage unavailable */
  }
}

/** Right-hand rail beside a graph canvas, resizable by dragging the splitter. */
export function useResizableRail(storageKey: string) {
  const [width, setWidth] = useState(() => readWidth(storageKey));
  const [resizing, setResizing] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef(width);
  widthRef.current = width;

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const body = bodyRef.current;
      if (!body) return;
      const startX = event.clientX;
      const startWidth = widthRef.current;
      // Never let the rail take more than ~55% of the graph body.
      const maxForBody = Math.max(
        RAIL_WIDTH_MIN,
        Math.min(RAIL_WIDTH_MAX, Math.floor(body.getBoundingClientRect().width * 0.55))
      );
      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);
      setResizing(true);

      const onMove = (ev: PointerEvent) => {
        const next = Math.min(maxForBody, Math.max(RAIL_WIDTH_MIN, startWidth + startX - ev.clientX));
        setWidth(next);
      };
      const onUp = (ev: PointerEvent) => {
        target.releasePointerCapture(ev.pointerId);
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
        target.removeEventListener('pointercancel', onUp);
        setResizing(false);
        writeWidth(storageKey, widthRef.current);
      };
      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
      target.addEventListener('pointercancel', onUp);
    },
    [storageKey]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const step = event.shiftKey ? 24 : 8;
      const next = clampWidth(widthRef.current + (event.key === 'ArrowLeft' ? step : -step));
      setWidth(next);
      writeWidth(storageKey, next);
    },
    [storageKey]
  );

  const bodyProps = {
    ref: bodyRef,
    style: { '--c3-attack-rail-width': `${width}px` } as React.CSSProperties,
  };

  const splitter = (
    <div
      className={`c3-graph-splitter${resizing ? ' is-dragging' : ''}`}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize side column"
      aria-valuemin={RAIL_WIDTH_MIN}
      aria-valuemax={RAIL_WIDTH_MAX}
      aria-valuenow={width}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    />
  );

  return { width, resizing, bodyProps, splitter };
}
